import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, ListFilter, LayoutGrid, Rows3, ArrowDownUp,
  ChevronRight, ChevronLeft, Search, FileSpreadsheet, Trash2
} from "lucide-react";
import { toast } from "sonner";
import Flag from "lucide-react/dist/esm/icons/flag";
import { api } from "../lib/api";
import { gbp, ukDate } from "../lib/format";
import { PageHeader, Card, PrimaryButton, QuietButton, EmptyState } from "../components/UI";
import StatusPill from "../components/StatusPill";
import OutcomeChip from "../components/OutcomeChip";
import BrandBadge from "../components/BrandBadge";
import { useAuth } from "../contexts/AuthContext";
import { useJobTypeLibrary } from "../hooks/useJobTypeLibrary";

function FilterChip({ label, count, active, onClick }) {
  return (
    <button type="button" onClick={onClick}
      className={[
        "inline-flex items-center gap-1.5 h-7 px-3 rounded-full transition-colors",
        active ? "bg-ink text-bg" : "bg-surface text-ink-2 border border-line hover:bg-surface-2"
      ].join(" ")} style={{ fontSize: 12, fontWeight: 500 }}>
      {label}
      <span className={["font-mono tnum", active ? "text-bg/70" : "text-ink-3"].join(" ")} style={{ fontSize: 10.5 }}>{count}</span>
    </button>
  );
}

function ViewToggle({ value, onChange }) {
  const opt = (key, Icon) => (
    <button key={key} onClick={() => onChange(key)}
      className={["h-7 w-7 flex items-center justify-center rounded transition-colors",
        value === key ? "bg-surface text-ink shadow-soft" : "text-ink-3 hover:text-ink"].join(" ")}>
      <Icon size={14} />
    </button>
  );
  return <div className="inline-flex items-center p-0.5 rounded-md border border-line bg-surface-2">
    {opt("table", Rows3)}{opt("cards", LayoutGrid)}
  </div>;
}

export default function TendersList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { labelFor: jobTypeLabelFor } = useJobTypeLibrary();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [view, setView]     = useState("table");
  const [q, setQ] = useState("");
  // LT-Archive-Trash · when filter === "trash", we hit list_tenders with
  // view=trash which the backend allows admin-only. Any other filter
  // uses the default view=active (excludes trashed tenders).
  const isTrashTab = filter === "trash";

  const load = () => {
    setLoading(true);
    const params = { q: q || undefined };
    if (isTrashTab) params.view = "trash";
    api.get("/tenders", { params })
      .then((r) => setItems(r.data || []))
      .catch(() => toast.error(isTrashTab ? "Failed to load Trash" : "Failed to load tenders"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []); // eslint-disable-line
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, filter]); // eslint-disable-line

  const counts = items.reduce(
    (acc, t) => ((acc[t.status] = (acc[t.status] || 0) + 1), acc),
    { all: items.length }
  );
  // Phase 0.5 — a tender "carries flags" if any line is blind or review.
  const hasFlags = (t) => ((t.flag_counts?.blind || 0) + (t.flag_counts?.review || 0)) > 0;
  counts.flagged = items.filter(hasFlags).length;
  // On the Trash tab the entire list IS the visible set (backend already
  // filtered). On other tabs, filter client-side by status.
  const visible = isTrashTab
    ? items
    : filter === "flagged"
      ? items.filter(hasFlags)
      : (filter === "all" ? items : items.filter((t) => t.status === filter));
  const totalValue = visible.reduce((s, t) => s + (t.estimated_value || 0), 0);

  return (
    <div className="app-container py-8">
      <div className="flex items-center gap-1.5 text-ink-3 mb-3" style={{ fontSize: 12 }}>
        <span>Workspace</span>
        <ChevronRight size={12} />
        <span className="text-ink-2">Tenders</span>
      </div>

      <PageHeader
        title="Tenders"
        subtitle="All live, submitted and historic tenders across both brands."
        actions={
          <>
            <QuietButton icon={ArrowDownUp}>Sort: Updated</QuietButton>
            <QuietButton icon={ListFilter}>More filters</QuietButton>
            <PrimaryButton icon={Plus} onClick={() => navigate("/tenders/new")}>Create new tender</PrimaryButton>
          </>
        }
      />

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          {[["all","All"],["active","Active"],["submitted","Submitted"],["won","Won"],["lost","Lost"],["draft","Draft"],["archived","Archived"]].map(([k,l]) => (
            <FilterChip key={k} label={l} count={counts[k] || 0} active={filter === k} onClick={() => setFilter(k)} />
          ))}
          <button
            type="button"
            onClick={() => setFilter("flagged")}
            data-testid="tenders-flagged-tab"
            className={[
              "inline-flex items-center gap-1.5 h-7 px-3 rounded-full transition-colors",
              filter === "flagged"
                ? "bg-indigo-600 text-white"
                : "bg-surface text-indigo-700 border border-indigo-300 hover:bg-indigo-50",
            ].join(" ")}
            style={{ fontSize: 12, fontWeight: 500 }}
          >
            <Flag size={11} /> Flagged
            <span className="font-mono tnum" style={{ fontSize: 10.5, opacity: 0.8 }}>{counts.flagged || 0}</span>
          </button>
          {isAdmin && (
            <button
              type="button"
              onClick={() => setFilter("trash")}
              data-testid="tenders-trash-tab"
              className={[
                "inline-flex items-center gap-1.5 h-7 px-3 rounded-full transition-colors",
                filter === "trash"
                  ? "bg-note-red text-white"
                  : "bg-surface text-note-red border border-note-red/30 hover:bg-note-red/5"
              ].join(" ")}
              style={{ fontSize: 12, fontWeight: 500 }}
            >
              <Trash2 size={11} /> Trash
              {isTrashTab && (
                <span className="font-mono tnum text-white/70" style={{ fontSize: 10.5 }}>
                  {items.length}
                </span>
              )}
            </button>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-4" />
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search tenders…" spellCheck="true"
              className="h-7 pl-7 pr-3 rounded-md border border-line bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
              style={{ fontSize: 12, width: 220 }} />
          </div>
          <ViewToggle value={view} onChange={setView} />
        </div>
      </div>

      <Card>
        {loading ? (
          <div className="py-12 text-center text-ink-3" style={{ fontSize: 12 }}>Loading…</div>
        ) : visible.length === 0 ? (
          <EmptyState
            icon={FileSpreadsheet}
            title={items.length === 0 ? "No tenders yet" : "No tenders match this filter"}
            hint={items.length === 0 ? "Create your first tender to get started." : "Try a different status filter."}
            action={items.length === 0 ? <PrimaryButton icon={Plus} onClick={() => navigate("/tenders/new")}>Create new tender</PrimaryButton> : null}
          />
        ) : (
          <>
            <div className="overflow-x-auto scrollbar-thin">
              <table className="w-full text-left" style={{ minWidth: 1100 }}>
                <thead>
                  <tr className="border-b border-line bg-surface-2/50">
                    <th className="col-head px-4 py-2.5" style={{ width: 90 }}>Ref</th>
                    <th className="col-head px-4 py-2.5">Client</th>
                    <th className="col-head px-4 py-2.5">Site address</th>
                    <th className="col-head px-4 py-2.5" style={{ width: 70 }}>Brand</th>
                    <th className="col-head px-4 py-2.5">Job type</th>
                    <th className="col-head px-4 py-2.5" style={{ width: 110 }}>Status</th>
                    <th className="col-head px-4 py-2.5" style={{ width: 150 }}>Outcome</th>
                    <th className="col-head px-4 py-2.5 text-right" style={{ width: 130 }}>Value</th>
                    <th className="col-head px-4 py-2.5" style={{ width: 100 }}>Created</th>
                    <th className="col-head px-4 py-2.5" style={{ width: 100 }}>Updated</th>
                    <th style={{ width: 40 }} />
                  </tr>
                </thead>
                <tbody>
                  {visible.map((t, i) => (
                    <tr key={t.id} onClick={() => navigate(`/tenders/${t.id}`)}
                      className={["group cursor-pointer hover:bg-surface-2/60",
                        i !== visible.length - 1 ? "border-b border-line-soft" : ""].join(" ")}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <span className="font-mono tnum text-ink" style={{ fontSize: 12.5 }}>{t.ref}</span>
                          {hasFlags(t) && (
                            <span
                              data-testid={`tender-flag-badge-${t.id}`}
                              title={`${t.flag_counts?.blind || 0} assumed, ${t.flag_counts?.review || 0} for review`}
                              className="inline-flex items-center gap-0.5 cursor-help"
                              style={{ color: (t.flag_counts?.blind || 0) > 0 ? "#B45309" : "#0F766E" }}
                            >
                              <Flag size={10} />
                              <span className="font-mono tnum" style={{ fontSize: 10 }}>
                                {(t.flag_counts?.blind || 0) + (t.flag_counts?.review || 0)}
                              </span>
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3"><div className="text-ink" style={{ fontSize: 13, fontWeight: 500 }}>{t.client_name}</div></td>
                      <td className="px-4 py-3">
                        <div className="text-ink-2" style={{ fontSize: 12.5 }}>
                          {[t.site_address_line_1, t.site_city].filter(Boolean).join(", ")}
                        </div>
                        <div className="text-ink-3 font-mono tnum mt-0.5" style={{ fontSize: 11 }}>{t.site_postcode}</div>
                      </td>
                      <td className="px-4 py-3"><BrandBadge brand={t.brand === "lux_construction" ? "CON" : "DEV"} /></td>
                      <td className="px-4 py-3"><span className="text-ink-2" style={{ fontSize: 12.5 }}>{jobTypeLabelFor(t.job_type) || "—"}</span></td>
                      <td className="px-4 py-3"><StatusPill status={t.status} /></td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        {/* LT-Estimate-UX-01 Section 8 dependency — inline
                            won / lost marker per row. Same disabled rules
                            as the header chip. `stopPropagation` so the
                            row-click nav doesn't fire while marking. */}
                        <OutcomeChip
                          tender={t}
                          variant="row"
                          onOutcomeChanged={(res) => {
                            setItems((prev) => prev.map((r) => (
                              r.id === t.id
                                ? { ...r, status: res.status,
                                    outcome_recorded_at: res.outcome_recorded_at,
                                    outcome_note: res.outcome_note }
                                : r
                            )));
                          }}
                          disabled={t.status === "draft" || t.status === "archived" || !!t.deleted_at}
                        />
                      </td>
                      <td className="px-4 py-3 text-right"><span className="font-mono tnum text-ink" style={{ fontSize: 13, fontWeight: 500 }}>{gbp(t.estimated_value || 0)}</span></td>
                      <td className="px-4 py-3"><span className="font-mono tnum text-ink-3" style={{ fontSize: 12 }}>{ukDate(t.created_at)}</span></td>
                      <td className="px-4 py-3"><span className="font-mono tnum text-ink-2" style={{ fontSize: 12 }}>{ukDate(t.updated_at)}</span></td>
                      <td className="pr-4 py-3 text-right"><ChevronRight size={14} className="text-ink-4 group-hover:text-ink-2" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between px-4 py-3 bg-surface-2/40 border-t border-line">
              <div className="text-ink-3" style={{ fontSize: 12 }}>
                Showing <span className="text-ink font-medium tnum font-mono">{visible.length}</span>{" "}of {items.length} tenders
              </div>
              <div className="flex items-center gap-6">
                <div className="flex items-baseline gap-2">
                  <span className="col-head">Total pipeline</span>
                  <span className="font-mono tnum text-ink" style={{ fontSize: 13, fontWeight: 600 }}>{gbp(totalValue)}</span>
                </div>
                <div className="flex items-center gap-1">
                  <button className="h-7 w-7 rounded-md border border-line bg-surface text-ink-3"><ChevronLeft size={13} className="mx-auto" /></button>
                  <span className="text-ink-3 px-2" style={{ fontSize: 12 }}>1 / 1</span>
                  <button className="h-7 w-7 rounded-md border border-line bg-surface text-ink-3"><ChevronRight size={13} className="mx-auto" /></button>
                </div>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
